import { useCallback, useEffect, useState, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import useUser, { getAuthorized, isUserClient, isUserDoctor, postAuthorized } from "../../../hooks/User";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import { convertToUserTimezone } from "../../../utils/timezone";
import SEO from "../../../components/SEO";

import "./mymedicationreminders.css";

// Icons
const PillIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"/>
        <path d="m8.5 8.5 7 7"/>
    </svg>
);

const ClockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
    </svg>
);

const CalendarIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
);

const BellIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
    </svg>
);

const HistoryIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v5h5"/>
        <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
        <path d="M12 7v5l4 2"/>
    </svg>
);

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18"/>
        <path d="m6 6 12 12"/>
    </svg>
);

const SnoozeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
        <path d="M22 4v6h-6"/>
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6"/>
    </svg>
);

const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
);

// Status configs
const ReminderStatusConfig = {
    pending: { label: "Ожидает", className: "status-pending", icon: "⏳" },
    sent: { label: "Требует ответа", className: "status-sent", icon: "🔔" },
    snoozed: { label: "Отложено", className: "status-snoozed", icon: "⏰" },
    responded: { label: "Отвечено", className: "status-responded", icon: "✓" },
    missed: { label: "Пропущено", className: "status-missed", icon: "⚠️" }
};

const IntakeStatusConfig = {
    taken: { 
        label: "Принято", 
        className: "status-taken",
        color: "var(--success, #1e9d6b)"
    },
    not_taken: { 
        label: "Не принято", 
        className: "status-not-taken",
        color: "var(--danger, #d43b3b)"
    },
    skipped: { 
        label: "Пропущено", 
        className: "status-skipped",
        color: "var(--warning, #e39c23)"
    }
};

const SkipReasonLabels = {
    side_effects: "Побочные эффекты",
    forgot: "Забыл",
    not_available: "Нет лекарства",
    feeling_bad: "Плохое самочувствие",
    other: "Другое"
};

// Tab Switcher Component
function TabSwitcher({ activeTab, onTabChange, navigate }) {
    return (
        <div className="medications-tab-switcher">
            <button
                className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
                onClick={() => onTabChange('active')}
            >
                <BellIcon />
                <span>Активные</span>
            </button>
            <button
                className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => onTabChange('history')}
            >
                <HistoryIcon />
                <span>История</span>
            </button>
            <button
                className="tab-btn"
                onClick={() => navigate(ROUTES.MY_MEDICATION_CALENDAR)}
            >
                <CalendarIcon />
                <span>Календарь</span>
            </button>
        </div>
    );
}

// Active Reminder Card Component
function ReminderCard({ reminder, onRespond, onSnooze, isLoading }) {
    const status = ReminderStatusConfig[reminder.status] || ReminderStatusConfig.sent;
    const isSent = reminder.status === "sent";
    const isSnoozed = reminder.status === "snoozed";
    const canSnooze = reminder.can_snooze;
    const canRespond = reminder.can_respond;
    
    const scheduledTime = convertToUserTimezone(reminder.scheduled_time);
    const doseTime = reminder.dose_time 
        ? convertToUserTimezone(reminder.dose_time)
        : scheduledTime;
    
    const isOverdue = dayjs().isAfter(scheduledTime.add(30, 'minute'));
    
    return (
        <div className={`reminder-card ${isOverdue ? 'overdue' : ''} ${isSnoozed ? 'snoozed' : ''}`}>
            <div className="reminder-card-header">
                <div className="reminder-icon">
                    <PillIcon />
                </div>
                <div className={`reminder-status-badge ${status.className}`}>
                    <span className="status-icon">{status.icon}</span>
                    <span>{status.label}</span>
                </div>
            </div>
            
            <div className="reminder-content">
                <h3 className="reminder-medication-name">{reminder.medication_name}</h3>
                <p className="reminder-dosage">{reminder.dosage}</p>
                
                {reminder.instructions && (
                    <div className="reminder-instructions">
                        <InfoIcon />
                        <span>{reminder.instructions}</span>
                    </div>
                )}
            </div>
            
            <div className="reminder-time-info">
                <div className="time-row primary">
                    <ClockIcon />
                    <span className="time-label">Время приёма:</span>
                    <span className="time-value">{doseTime.format("HH:mm")}</span>
                </div>
                <div className="time-row">
                    <CalendarIcon />
                    <span className="time-label">{doseTime.format("DD.MM.YYYY")}</span>
                </div>
                {reminder.snooze_count > 0 && (
                    <div className="snooze-info">
                        Отложено: {reminder.snooze_count} раз
                    </div>
                )}
            </div>
            
            {canRespond && (
                <div className="reminder-actions">
                    <button 
                        className="btn-respond btn-taken"
                        onClick={() => onRespond(reminder, 'taken')}
                        disabled={isLoading}
                    >
                        <CheckIcon />
                        <span>Принял</span>
                    </button>
                    <button 
                        className="btn-respond btn-skipped"
                        onClick={() => onRespond(reminder, 'skipped')}
                        disabled={isLoading}
                    >
                        <XIcon />
                        <span>Пропустил</span>
                    </button>
                    {canSnooze && (
                        <button 
                            className="btn-respond btn-snooze"
                            onClick={() => onSnooze(reminder)}
                            disabled={isLoading}
                            title="Напомнить позже"
                        >
                            <SnoozeIcon />
                            <span>Позже</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// Intake History Card Component
function IntakeCard({ intake }) {
    const status = IntakeStatusConfig[intake.status] || IntakeStatusConfig.skipped;
    const scheduledTime = convertToUserTimezone(intake.scheduled_time);
    
    return (
        <div className={`intake-card status-${intake.status}`}>
            <div className="intake-card-header">
                <div className="intake-status-badge" style={{ color: status.color }}>
                    <div className="status-dot" style={{ background: status.color }}></div>
                    <span>{status.label}</span>
                </div>
                <span className="intake-date">
                    {scheduledTime.format("DD.MM.YYYY HH:mm")}
                </span>
            </div>
            
            <div className="intake-content">
                <h3 className="intake-medication">{intake.medication_name || `ID: ${intake.schedule_id}`}</h3>
                
                {intake.actual_dosage && (
                    <p className="intake-dosage">
                        Доза: {intake.actual_dosage}
                    </p>
                )}
                
                {intake.status === 'skipped' && intake.skip_reason && (
                    <div className="skip-reason">
                        <span className="reason-label">
                            {intake.skip_reason_category 
                                ? SkipReasonLabels[intake.skip_reason_category] || intake.skip_reason_category
                                : 'Причина'}:
                        </span>
                        <span className="reason-text">{intake.skip_reason}</span>
                    </div>
                )}
                
                {intake.notes && (
                    <p className="intake-notes">{intake.notes}</p>
                )}
            </div>
            
            {intake.responded_at && (
                <div className="intake-footer">
                    <span>Ответ: {dayjs(intake.responded_at).format("HH:mm")}</span>
                </div>
            )}
        </div>
    );
}

// Response Modal Component
function ResponseModal({ reminder, responseType, onClose, onSubmit, isLoading }) {
    const [formData, setFormData] = useState({
        actual_dosage: reminder?.dosage || "",
        taken_at_minutes_ago: 0,
        notes: "",
        skip_reason: "",
        skip_reason_category: "other"
    });
    
    if (!reminder) return null;
    
    const isSkipped = responseType === 'skipped';
    
    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            response_type: responseType,
            ...formData
        };
        if (!isSkipped) {
            delete payload.skip_reason;
            delete payload.skip_reason_category;
        }
        onSubmit(reminder.id, payload);
    };
    
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isSkipped ? 'Пропуск приёма' : 'Подтверждение приёма'}</h2>
                    <button className="modal-close" onClick={onClose}>
                        <XIcon />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="reminder-summary">
                            <div className="summary-icon">
                                <PillIcon />
                            </div>
                            <div className="summary-info">
                                <h4>{reminder.medication_name}</h4>
                                <p>{reminder.dosage}</p>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <label>{isSkipped ? 'Фактическая доза' : 'Принятая доза'}</label>
                            <input
                                type="text"
                                value={formData.actual_dosage}
                                onChange={(e) => setFormData(prev => ({ ...prev, actual_dosage: e.target.value }))}
                                placeholder="Например: 1 таблетка"
                            />
                        </div>
                        
                        {!isSkipped && (
                            <div className="form-group">
                                <label>Принято (минут назад)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="120"
                                    value={formData.taken_at_minutes_ago}
                                    onChange={(e) => setFormData(prev => ({ ...prev, taken_at_minutes_ago: parseInt(e.target.value) || 0 }))}
                                />
                            </div>
                        )}
                        
                        {isSkipped && (
                            <>
                                <div className="form-group">
                                    <label>Причина пропуска *</label>
                                    <select
                                        value={formData.skip_reason_category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, skip_reason_category: e.target.value }))}
                                        required
                                    >
                                        <option value="forgot">Забыл</option>
                                        <option value="not_available">Нет лекарства</option>
                                        <option value="side_effects">Побочные эффекты</option>
                                        <option value="feeling_bad">Плохое самочувствие</option>
                                        <option value="other">Другое</option>
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <label>Описание причины</label>
                                    <textarea
                                        value={formData.skip_reason}
                                        onChange={(e) => setFormData(prev => ({ ...prev, skip_reason: e.target.value }))}
                                        placeholder="Уточните причину пропуска..."
                                        rows={2}
                                    />
                                </div>
                            </>
                        )}
                        
                        <div className="form-group">
                            <label>Заметки</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="Дополнительные заметки..."
                                rows={2}
                            />
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>
                            Отмена
                        </button>
                        <button 
                            type="submit" 
                            className={`btn-primary ${isSkipped ? 'btn-danger' : 'btn-success'}`}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span className="loading-dots">Сохранение...</span>
                            ) : (
                                <>
                                    {isSkipped ? <XIcon /> : <CheckIcon />}
                                    <span>{isSkipped ? 'Подтвердить пропуск' : 'Подтвердить приём'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Statistics Component
function Statistics({ intakes }) {
    const total = intakes.length;
    const taken = intakes.filter(i => i.status === 'taken').length;
    const skipped = intakes.filter(i => i.status === 'skipped').length;
    const notTaken = intakes.filter(i => i.status === 'not_taken').length;
    
    const takenPercent = total > 0 ? Math.round((taken / total) * 100) : 0;
    
    return (
        <div className="statistics-section">
            <h3>Статистика за период</h3>
            <div className="stats-grid">
                <div className="stat-card stat-total">
                    <span className="stat-value">{total}</span>
                    <span className="stat-label">Всего записей</span>
                </div>
                <div className="stat-card stat-taken">
                    <span className="stat-value">{taken}</span>
                    <span className="stat-label">Принято</span>
                    <span className="stat-percent">{takenPercent}%</span>
                </div>
                <div className="stat-card stat-skipped">
                    <span className="stat-value">{skipped}</span>
                    <span className="stat-label">Пропущено</span>
                </div>
                <div className="stat-card stat-missed">
                    <span className="stat-value">{notTaken}</span>
                    <span className="stat-label">Не принято</span>
                </div>
            </div>
        </div>
    );
}

// Active Reminders View
function ActiveRemindersView({ reminders, loading, onRespond, onSnooze, respondingId, snoozingId }) {
    // Sort: sent first, then by time
    const sortedReminders = [...reminders].sort((a, b) => {
        const statusOrder = { sent: 0, snoozed: 1, pending: 2, responded: 3, missed: 4 };
        const statusDiff = (statusOrder[a.status] || 5) - (statusOrder[b.status] || 5);
        if (statusDiff !== 0) return statusDiff;
        
        return dayjs(a.scheduled_time).diff(dayjs(b.scheduled_time));
    });
    
    const pendingCount = reminders.filter(r => r.status === 'sent' || r.status === 'snoozed').length;
    
    return (
        <>
            <div className="medications-header-section">
                <div className="medications-title">
                    <BellIcon />
                    <div>
                        <h1>Напоминания о лекарствах</h1>
                        <p className="medications-subtitle">
                            {pendingCount > 0 
                                ? `${pendingCount} требует${pendingCount === 1 ? 'ся' : 'ься'} вашего ответа`
                                : 'Все напоминания обработаны'
                            }
                        </p>
                    </div>
                </div>
            </div>
            
            {loading ? (
                <div className="medications-loading">
                    <div className="spinner"></div>
                    <p>Загрузка напоминаний...</p>
                </div>
            ) : sortedReminders.length === 0 ? (
                <div className="empty-medications-state">
                    <div className="empty-icon">
                        <BellIcon />
                    </div>
                    <h3>Нет активных напоминаний</h3>
                    <p>На данный момент нет напоминаний, требующих вашего ответа</p>
                </div>
            ) : (
                <div className="reminders-grid">
                    {sortedReminders.map(reminder => (
                        <ReminderCard
                            key={reminder.id}
                            reminder={reminder}
                            onRespond={onRespond}
                            onSnooze={onSnooze}
                            isLoading={respondingId === reminder.id || snoozingId === reminder.id}
                        />
                    ))}
                </div>
            )}
        </>
    );
}

// History View
function HistoryView({ intakes, loading, hasMore, onLoadMore }) {
    return (
        <>
            <div className="medications-header-section">
                <div className="medications-title">
                    <HistoryIcon />
                    <div>
                        <h1>История приёма лекарств</h1>
                        <p className="medications-subtitle">
                            Все записи о принятых и пропущенных дозах
                        </p>
                    </div>
                </div>
            </div>
            
            {!loading && intakes.length > 0 && <Statistics intakes={intakes} />}
            
            {loading && intakes.length === 0 ? (
                <div className="medications-loading">
                    <div className="spinner"></div>
                    <p>Загрузка истории...</p>
                </div>
            ) : intakes.length === 0 ? (
                <div className="empty-medications-state">
                    <div className="empty-icon">
                        <HistoryIcon />
                    </div>
                    <h3>История пуста</h3>
                    <p>Вы ещё не отвечали на напоминания о приёме лекарств</p>
                </div>
            ) : (
                <>
                    <div className="intakes-grid">
                        {intakes.map(intake => (
                            <IntakeCard key={intake.id} intake={intake} />
                        ))}
                    </div>
                    
                    {hasMore && (
                        <div className="load-more-container">
                            <button 
                                className="btn-load-more"
                                onClick={onLoadMore}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <div className="spinner-small"></div>
                                        Загрузка...
                                    </>
                                ) : (
                                    <>
                                        Загрузить ещё
                                        <ChevronRightIcon />
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </>
            )}
        </>
    );
}

// Main Component
function MyMedicationReminders() {
    const navigate = useNavigate();
    const { setErrorList } = useContext(ErrorHandlerContext);

    // Tab state
    const [activeTab, setActiveTab] = useState('active');
    
    // Active reminders state
    const [reminders, setReminders] = useState([]);
    const [loadingReminders, setLoadingReminders] = useState(true);
    const [respondingId, setRespondingId] = useState(null);
    const [snoozingId, setSnoozingId] = useState(null);
    
    // Response modal state
    const [responseModal, setResponseModal] = useState({
        isOpen: false,
        reminder: null,
        responseType: null
    });
    
    // History state
    const [intakes, setIntakes] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 20;

    // Load active reminders
    const loadReminders = useCallback(async () => {
        setLoadingReminders(true);
        try {
            const response = await getAuthorized("/client/my_medication_reminders", {
                params: { include_answered: false }
            });
            setReminders(response.data || []);
        } catch (error) {
            console.error("Failed to load reminders:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке напоминаний"]);
        } finally {
            setLoadingReminders(false);
        }
    }, [setErrorList]);

    // Load intake history
    const loadIntakes = useCallback(async (reset = false) => {
        if (reset) {
            setPage(0);
            setIntakes([]);
        }
        
        setLoadingHistory(true);
        try {
            const params = {
                limit: PAGE_SIZE,
                offset: reset ? 0 : page * PAGE_SIZE
            };
            
            const response = await getAuthorized("/client/my_medication_intakes", { params });
            
            const newIntakes = response.data || [];
            
            if (reset) {
                setIntakes(newIntakes);
            } else {
                setIntakes(prev => [...prev, ...newIntakes]);
            }
            
            setHasMore(newIntakes.length === PAGE_SIZE);
            
            if (!reset) {
                setPage(prev => prev + 1);
            }
        } catch (error) {
            console.error("Failed to load intakes:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при загрузке истории"]);
        } finally {
            setLoadingHistory(false);
        }
    }, [page, setErrorList]);

    // Load data when tab changes
    useEffect(() => {
        if (activeTab === 'active') {
            loadReminders();
        } else if (activeTab === 'history') {
            loadIntakes(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Handle respond click - open modal
    const handleRespondClick = useCallback((reminder, responseType) => {
        setResponseModal({
            isOpen: true,
            reminder,
            responseType
        });
    }, []);

    // Handle respond submit
    const handleRespondSubmit = useCallback(async (reminderId, payload) => {
        setRespondingId(reminderId);
        try {
            await postAuthorized('/client/respond_medication_reminder/' + reminderId, payload);
            setResponseModal({ isOpen: false, reminder: null, responseType: null });
            loadReminders();
            // Also refresh history if on that tab
            if (activeTab === 'history') {
                loadIntakes(true);
            }
        } catch (error) {
            console.error("Failed to respond:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при сохранении ответа"]);
        } finally {
            setRespondingId(null);
        }
    }, [loadReminders, loadIntakes, activeTab, setErrorList]);

    // Handle snooze
    const handleSnooze = useCallback(async (reminder) => {
        setSnoozingId(reminder.id);
        try {
            await postAuthorized('/client/snooze_medication_reminder/' + reminder.id + '/snooze');
            loadReminders();
        } catch (error) {
            console.error("Failed to snooze:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при откладывании"]);
        } finally {
            setSnoozingId(null);
        }
    }, [loadReminders, setErrorList]);

    const handleCloseModal = useCallback(() => {
        setResponseModal({ isOpen: false, reminder: null, responseType: null });
    }, []);

    const handleLoadMore = () => {
        loadIntakes(false);
    };

    return (
        <div className="my-medications-container">
            <TabSwitcher activeTab={activeTab} onTabChange={setActiveTab} navigate={navigate} />
            
            {activeTab === 'active' ? (
                <ActiveRemindersView
                    reminders={reminders}
                    loading={loadingReminders}
                    onRespond={handleRespondClick}
                    onSnooze={handleSnooze}
                    respondingId={respondingId}
                    snoozingId={snoozingId}
                />
            ) : (
                <HistoryView
                    intakes={intakes}
                    loading={loadingHistory}
                    hasMore={hasMore}
                    onLoadMore={handleLoadMore}
                />
            )}
            
            <ResponseModal
                reminder={responseModal.reminder}
                responseType={responseModal.responseType}
                isOpen={responseModal.isOpen}
                onClose={handleCloseModal}
                onSubmit={handleRespondSubmit}
                isLoading={respondingId !== null}
            />
        </div>
    );
}

// Page Component with Error Handler
function MyMedicationRemindersPage() {
    const { data, loading, error } = useUser();
    const navigate = useNavigate();

    useEffect(() => {
        if (loading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        // Allow access to all users with client role (including doctors who are also patients)
        if (data && !isUserClient(data)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [data, error, loading, navigate]);

    if (loading) {
        return (
            <>
                <SEO title="Мои напоминания" description="Управление напоминаниями о приеме лекарств" noindex={true} />
                <ProfileNavigation isDoctor={false} />
                <div className="my-medications-page">
                    <div className="medications-loading">
                        <div className="spinner"></div>
                        <p>Загрузка...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <SEO title="Мои напоминания" description="Управление напоминаниями о приеме лекарств" noindex={true} />
            <ErrorHandlerContextProvider>
                <ProfileNavigation isDoctor={isUserDoctor(data)} />
                <div className="my-medications-page">
                    <MyMedicationReminders />
                </div>
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}

export default MyMedicationRemindersPage;
